import React, { useEffect, useRef, useState } from 'react';
import CloseIcon from '../../images/CloseIcon';
import { useDialogFocus } from '../../hook/useFormKeyboard';
import { BORDER, BORDER_SOFT, BORDER_STRONG, MONO, Pill, TEAL, TEXT, TEXT_DIM, TEXT_MUTED, t } from '../../components/list';
import {
  BrandMark,
  CardPaymentView,
  ContactlessMark,
  INK,
  SalePayment,
  attemptsInOrder,
  cardBrandOf,
  endingIn,
  formatClock,
  formatDay,
  humanize,
  isContactless,
  money,
  primaryAttempt,
  statusTone,
  toDate,
  toNumber,
} from './recentSalesShared';

// Inline <style> like TableSkeleton: the host build has no module CSS. Motion stays off under reduced motion.
const DIALOG_CSS = `
@keyframes rs-card-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes rs-card-sheet { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: none; } }
@keyframes rs-card-pop { from { opacity: 0; transform: translateY(8px) scale(.98); } to { opacity: 1; transform: none; } }
.rs-card-backdrop { animation: rs-card-fade 180ms ease-out both; }
.rs-card-sheet { animation: rs-card-sheet 220ms cubic-bezier(.2,.8,.2,1) both; }
.rs-card-pop { animation: rs-card-pop 200ms cubic-bezier(.2,.8,.2,1) both; }
.rs-card-icon-btn { background-color: transparent; }
.rs-card-copy { background-color: #FFFFFF; }
.rs-card-icon-btn:hover, .rs-card-copy:hover { background-color: #EBF0F0; }
.rs-card-icon-btn:focus-visible, .rs-card-copy:focus-visible { outline: 2px solid #0F7679; outline-offset: 2px; }
@media (prefers-reduced-motion: reduce) {
  .rs-card-backdrop, .rs-card-sheet, .rs-card-pop { animation: none; }
}
`;

const VISUALLY_HIDDEN: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};

// execCommand fallback for http and older browsers; the textarea stays inside the dialog so focus does too.
const legacyCopy = (text: string, host: HTMLElement): boolean => {
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    Object.assign(area.style, { position: 'fixed', top: '0', left: '0', opacity: '0' });
    host.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    host.removeChild(area);
    return ok;
  } catch {
    return false;
  }
};

const copyText = (text: string, host: HTMLElement): Promise<boolean> => {
  if (navigator.clipboard?.writeText && window.isSecureContext) {
    return navigator.clipboard.writeText(text).then(
      () => true,
      () => legacyCopy(text, host)
    );
  }
  return Promise.resolve(legacyCopy(text, host));
};

const CopyIcon: React.FC<{ done: boolean }> = ({ done }) => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {done ? (
      <path d="M4 10.5l4 4 8-8.5" />
    ) : (
      <>
        <rect x="7" y="7" width="10" height="10" rx="2" />
        <path d="M13 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
      </>
    )}
  </svg>
);

/** A long id with a Copy button; when copying fails the id is selected for a manual copy. */
const CopyValue: React.FC<{ value: string; label: string; language: any }> = ({ value, label, language }) => {
  const [copied, setCopied] = useState(false);
  const valueRef = useRef<HTMLSpanElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );

  const onCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
    const button = e.currentTarget;
    const host = (button.closest('[role="dialog"]') as HTMLElement | null) ?? document.body;
    // writeText runs inside the click, so Safari still counts it as a user gesture.
    copyText(value, host).then(ok => {
      if (document.contains(button)) button.focus({ preventScroll: true });
      if (ok) {
        setCopied(true);
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), 1600);
      } else if (valueRef.current) {
        window.getSelection()?.selectAllChildren(valueRef.current);
      }
    });
  };

  return (
    <span style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', minWidth: 0 }}>
      <span ref={valueRef} style={{ fontFamily: MONO, fontSize: '13px', flex: 1, minWidth: 0, overflowWrap: 'anywhere', paddingTop: '3px' }}>
        {value}
      </span>
      <button
        type="button"
        className="rs-card-copy font-inter"
        onClick={onCopy}
        aria-label={`${t(language, 'Copy', 'Copy')}: ${label}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          flex: 'none',
          height: '26px',
          padding: '0 8px',
          borderRadius: '8px',
          border: `1px solid ${BORDER_STRONG}`,
          color: copied ? '#1F6B4A' : TEAL,
          fontSize: '12px',
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        <CopyIcon done={copied} />
        {copied ? t(language, 'Copied', 'Copied') : t(language, 'Copy', 'Copy')}
      </button>
      <span aria-live="polite" style={VISUALLY_HIDDEN}>
        {copied ? t(language, 'Copied', 'Copied') : ''}
      </span>
    </span>
  );
};

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: 'minmax(108px, 38%) minmax(0, 1fr)',
      gap: '12px',
      padding: '10px 0',
      borderTop: `1px solid ${BORDER_SOFT}`,
    }}
  >
    <dt style={{ fontSize: '13px', color: TEXT_DIM, lineHeight: '20px' }}>{label}</dt>
    <dd style={{ margin: 0, fontSize: '14px', fontWeight: 500, color: TEXT, lineHeight: '20px', minWidth: 0, overflowWrap: 'anywhere' }}>
      {children}
    </dd>
  </div>
);

/** EMV chip drawn in flat gold. */
const EmvChip = () => (
  <svg width="36" height="27" viewBox="0 0 36 27" aria-hidden="true" style={{ display: 'block' }}>
    <rect x="0.5" y="0.5" width="35" height="26" rx="5" fill="#D8B76A" stroke="rgba(0,0,0,.18)" />
    <path d="M12 1v25M24 1v25M1 9h11M24 9h11M1 18h11M24 18h11" stroke="rgba(0,0,0,.25)" strokeWidth="1" fill="none" />
  </svg>
);

const CardVisual: React.FC<{ attempt: CardPaymentView; language: any }> = ({ attempt, language }) => {
  const brand = cardBrandOf(attempt.cardType);
  const funding = humanize(attempt.fundingType);
  const fundingLabel = funding ? t(language, funding, funding) : '';
  const contactless = isContactless(attempt.entryMode);
  const lastFour = attempt.lastFour ?? '';
  const name = brand.name || t(language, 'Card', 'Card');
  const spoken = [name, fundingLabel, endingIn(lastFour, language), contactless ? t(language, 'Contactless', 'Contactless') : '']
    .filter(Boolean)
    .join(', ');
  return (
    <div
      role="img"
      aria-label={spoken}
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: '320px',
        aspectRatio: '1.586 / 1',
        minHeight: '168px',
        margin: '0 auto',
        borderRadius: '16px',
        background: brand.background,
        color: '#FFFFFF',
        padding: '18px 20px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        overflow: 'hidden',
        boxShadow: '0 12px 28px rgba(5,41,42,.20)',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(120% 90% at 100% 0%, rgba(255,255,255,.20) 0%, rgba(255,255,255,0) 55%)',
          pointerEvents: 'none',
        }}
      />
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <EmvChip />
        {contactless && <ContactlessMark size={24} />}
      </div>
      <div
        aria-hidden="true"
        style={{ position: 'relative', fontFamily: MONO, fontSize: 'clamp(14px, 4.6vw, 18px)', letterSpacing: '0.12em', whiteSpace: 'nowrap' }}
      >
        •••• •••• •••• {lastFour || '••••'}
      </div>
      <div aria-hidden="true" style={{ position: 'relative', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '12px' }}>
        <span style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.85 }}>
          {fundingLabel}
        </span>
        <BrandMark brand={brand} fallback={t(language, 'Card', 'Card')} />
      </div>
    </div>
  );
};

const AttemptDetails: React.FC<{ attempt: CardPaymentView; index: number; count: number; language: any }> = ({
  attempt,
  index,
  count,
  language,
}) => {
  const tr = (key: string) => t(language, key, key);
  const word = (value?: string | null) => {
    const h = humanize(value);
    return h ? t(language, h, h) : '';
  };
  const currency = attempt.currency;
  const captured = toDate(attempt.capturedAt);
  const statuses = [attempt.paymentStatus, attempt.responseStatus]
    .map(value => ({ value, label: word(value) }))
    .filter((s, i, all) => s.label && all.findIndex(o => o.label.toLowerCase() === s.label.toLowerCase()) === i);
  const terminal = [attempt.terminalId, attempt.tid ? `TID ${attempt.tid}` : ''].filter(Boolean).join(' · ');
  const attemptLabel = `${tr('Attempt')} ${index + 1} ${tr('of')} ${count}`;

  const rows: { label: string; value: React.ReactNode }[] = [
    { label: tr('Amount'), value: money(attempt.amount, currency) },
    { label: tr('Tip'), value: money(attempt.tip, currency) },
    { label: tr('Total'), value: money(attempt.totalAmount, currency) },
    { label: tr('Refunded amount'), value: toNumber(attempt.refundedAmount) > 0 ? money(attempt.refundedAmount, currency) : '' },
    { label: tr('Captured at'), value: captured ? `${formatDay(captured)} ${formatClock(captured, true)}` : '' },
    { label: tr('Auth code'), value: attempt.authCode ? <span style={{ fontFamily: MONO, fontSize: '13px' }}>{attempt.authCode}</span> : '' },
    { label: tr('Verification method'), value: word(attempt.verificationMethod) },
    { label: tr('Entry mode'), value: word(attempt.entryMode) },
    { label: tr('Terminal'), value: terminal ? <span style={{ fontFamily: MONO, fontSize: '13px' }}>{terminal}</span> : '' },
    {
      label: tr('Provider transaction ID'),
      value: attempt.providerTransactionId ? <span style={{ fontFamily: MONO, fontSize: '13px' }}>{attempt.providerTransactionId}</span> : '',
    },
    {
      label: tr('Provider payment ID'),
      value: attempt.providerPaymentId ? (
        <CopyValue
          value={attempt.providerPaymentId}
          label={count > 1 ? `${tr('Provider payment ID')} (${attemptLabel})` : tr('Provider payment ID')}
          language={language}
        />
      ) : (
        ''
      ),
    },
    {
      label: tr('Reference'),
      value: attempt.reference ? <span style={{ fontFamily: MONO, fontSize: '13px' }}>{attempt.reference}</span> : '',
    },
  ];

  return (
    <section
      aria-label={count > 1 ? attemptLabel : undefined}
      style={index > 0 ? { borderTop: `1px solid ${BORDER}`, paddingTop: '20px' } : undefined}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: count > 1 ? 'space-between' : 'flex-start', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
        {count > 1 && (
          <span style={{ fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.02em', color: TEXT_MUTED }}>
            {attemptLabel}
          </span>
        )}
        {statuses.length > 0 && (
          <span style={{ display: 'inline-flex', gap: '6px', flexWrap: 'wrap', minWidth: 0 }}>
            {statuses.map(s => (
              <Pill key={s.label} label={s.label} kind={statusTone(s.value)} />
            ))}
          </span>
        )}
      </div>
      <CardVisual attempt={attempt} language={language} />
      <dl style={{ margin: '16px 0 0' }}>
        {rows
          .filter(row => row.value !== '' && row.value !== null && row.value !== undefined)
          .map(row => (
            <DetailRow key={row.label} label={row.label}>
              {row.value}
            </DetailRow>
          ))}
      </dl>
    </section>
  );
};

interface CardPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  language: any;
  payment: SalePayment | null;
  /** Tender name of the payment, for the subtitle. */
  tenderName: string;
  isMobile: boolean;
}

/** Card attempts of one payment: a bottom sheet on mobile, a centred dialog on desktop. */
const CardPaymentDialog: React.FC<CardPaymentDialogProps> = ({ open, onClose, language, payment, tenderName, isMobile }) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const pressedBackdrop = useRef(false);
  const visible = open && !!payment;
  useDialogFocus(visible, dialogRef, onClose);

  useEffect(() => {
    if (!visible) return undefined;
    const before = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = before;
    };
  }, [visible]);

  if (!visible || !payment) return null;
  const attempts = attemptsInOrder(payment.cardPayments);
  const titleId = `rs-card-title-${payment.id}`;
  const subtitleId = `rs-card-sub-${payment.id}`;

  return (
    <div
      className="rs-card-backdrop font-inter"
      // Closes only when the press started on the backdrop too, so selecting an id never dismisses it.
      onMouseDown={e => {
        pressedBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={e => {
        if (pressedBackdrop.current && e.target === e.currentTarget) onClose();
        pressedBackdrop.current = false;
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
        padding: isMobile ? 0 : '24px',
        backgroundColor: 'rgba(5,41,42,.40)',
        fontFamily: 'Inter, sans-serif',
      }}
    >
      <style>{DIALOG_CSS}</style>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={subtitleId}
        tabIndex={-1}
        className={isMobile ? 'rs-card-sheet' : 'rs-card-pop'}
        style={{
          width: '100%',
          maxWidth: isMobile ? '100%' : '460px',
          maxHeight: isMobile ? '88vh' : 'min(760px, calc(100vh - 48px))',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#FFFFFF',
          borderRadius: isMobile ? '22px 22px 0 0' : '16px',
          boxShadow: isMobile ? '0 -12px 40px rgba(0,0,0,.14)' : '0 24px 60px rgba(5,41,42,.22)',
          outline: 'none',
          overflow: 'hidden',
        }}
      >
        {isMobile && (
          <div aria-hidden="true" style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 0', flex: 'none' }}>
            <span style={{ width: '40px', height: '4px', borderRadius: '999px', backgroundColor: '#DBE4E4' }} />
          </div>
        )}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            padding: isMobile ? '10px 20px 14px' : '18px 20px 14px 24px',
            borderBottom: `1px solid ${BORDER_SOFT}`,
            flex: 'none',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 id={titleId} style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: INK, lineHeight: '26px' }}>
              {t(language, 'Card details', 'Card details')}
            </h2>
            <p id={subtitleId} style={{ margin: '2px 0 0', fontSize: '13px', color: TEXT_DIM, overflowWrap: 'anywhere' }}>
              {tenderName} · {money(payment.paymentAmount, primaryAttempt(attempts)?.currency)}
            </p>
          </div>
          <button
            type="button"
            className="rs-card-icon-btn"
            onClick={onClose}
            aria-label={t(language, 'Close', 'Close')}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '999px',
              border: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 'none',
              cursor: 'pointer',
              marginRight: '-6px',
            }}
          >
            <CloseIcon />
          </button>
        </div>
        <div
          style={{
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            padding: isMobile ? '18px 20px calc(20px + env(safe-area-inset-bottom, 0px))' : '20px 24px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '28px',
            minHeight: 0,
          }}
        >
          {attempts.map((attempt, index) => (
            <AttemptDetails key={attempt.id ?? index} attempt={attempt} index={index} count={attempts.length} language={language} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default CardPaymentDialog;
